import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Logger } from "infra/logger/logger";
import { Distance } from "domain/entities/distance";
import distanceRepository from "infra/repositories/distance-repository";
import { DistanceInputFilter } from "domain/models/distance-input-filter";

export const useDistanceByFilter = (filter: DistanceInputFilter) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["distances", filter],
    queryFn: async () => {
      try {
        const result = await distanceRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata?.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

export const useCreateDistanceMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Distance>) => {
      const result = await distanceRepository.create(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["distances"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors;
        logger.error(errors?.[0], error.response?.data.metadata);
        const customError = new Error("Erro na criação da distância");
        (customError as any).errors = errors;
        throw customError;
      }
      throw error;
    },
  });
};

export const useDistanceById = (id?: string) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["distance", id],
    queryFn: async () => {
      try {
        if (!id) throw new Error("Id inválido");
        const result = await distanceRepository.getById(id);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata?.errors);
        }
        throw error;
      }
    },
    enabled: !!id,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

export const useUpdateDistanceMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Distance) => {
      const result = await distanceRepository.update(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["distances"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata?.errors?.[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata?.errors);
      }
      throw error;
    },
  });
};

export const useDeleteDistanceMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (distance: Distance) => {
      if (!distance?.id) throw new Error("ID inválido para exclusão");
      const result = await distanceRepository.delete(distance?.id);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["distances"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata?.errors?.[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata?.errors);
      }
      throw error;
    },
  });
};

export const usePopulateDistanceMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const result = await distanceRepository.populate();
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["distances"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors;
        logger.error(errors?.[0], error.response?.data.metadata);
        const customError = new Error("Erro na população das distâncias");
        (customError as any).errors = errors;
        throw customError;
      }
      throw error;
    },
  });
};
